import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { AuthService } from 'src/app/core/auth.service';
import { IdentityService } from 'src/app/core/identity.service';
import { LoggerService } from 'src/app/shared/services/logger.service';

@Injectable({
    providedIn: 'root'
})
export class TokenSilentRefreshService {
    private readonly _logPrefix = '[nova-token-silent-refresh] ';

    constructor(
        private readonly _authService: AuthService,
        private readonly _identityService: IdentityService,
        private readonly _loggerService: LoggerService,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {}

    silentRefresh(refreshTokenInProgress$: BehaviorSubject<boolean>): boolean {
        const accessTokenRefreshAvailableDate = this._authService.accessTokenSilentRefreshAvailableDate;

        if (!accessTokenRefreshAvailableDate) {
            this._loggerService.info(
                this._logPrefix + 'Skipping... failed to retrieve access token silent refresh available date.'
            );
            return false;
        }

        const now = new Date();
        const isAccessTokenSilentRefreshAvailable = now.getTime() > accessTokenRefreshAvailableDate.getTime();
        if (!isAccessTokenSilentRefreshAvailable) {
            return false;
        }

        const isTokenSilentRefreshLocked = this._identityService.isAccessTokenSilentRefreshLocked;
        if (isTokenSilentRefreshLocked) {
            this._loggerService.info(this._logPrefix + 'Skipping... lock is set.');
            return true;
        }

        this._identityService.isAccessTokenSilentRefreshLocked = true;
        refreshTokenInProgress$.next(true);
        this._loggerService.info(this._logPrefix + 'Refresh token is now locked');

        const timeoutComplete$ = new Subject();
        const failsafeTimeout = setTimeout(() => {
            refreshTokenInProgress$.next(false);
            timeoutComplete$.next(null);
            this._loggerService.error(this._logPrefix + '401 refresh flow failsafe unlock triggered.');
        }, this._settings?.failsafeTimeoutInMilliseconds ?? 5000);

        refreshTokenInProgress$.pipe(takeUntil(timeoutComplete$)).subscribe((isLocked) => {
            //stop subscribe after timer completes or after first false
            if (isLocked === false) {
                clearTimeout(failsafeTimeout);
                timeoutComplete$.next(null);
            }
        });

        this._loggerService.info(this._logPrefix + 'Refreshing...');
        this._authService.refreshToken(refreshTokenInProgress$).pipe(take(1)).subscribe();

        return true;
    }
}
